import { IconButton, Stack, Typography, useTheme } from "@mui/material";
import { Horizontal3DotsIcon } from "components/icons";
import { DropdownMenu } from "components/menu";
import type { ReactNode } from "react";
import { ChatRowTitleEditor } from "./ChatRowTitleEditor";

/** Action ids a conversation row hands back to its owner. */
export type ChatRowAction = "rename" | "pin" | "unpin" | "delete";

const menuOptions = (isPinned: boolean) => [
  { name: "Rename", value: "rename" },
  isPinned
    ? { name: "Unpin chat", value: "unpin" }
    : { name: "Pin chat", value: "pin" },
  { name: "Delete chat", value: "delete" },
];

const isChatRowAction = (value: string | undefined): value is ChatRowAction =>
  value === "rename" ||
  value === "pin" ||
  value === "unpin" ||
  value === "delete";

interface ChatSidebarItemProps {
  label: string;
  isActive?: boolean;
  /** Leading glyph — the action rows have one, history rows don't. */
  icon?: ReactNode;
  onClick?: () => void;
  /** Flips the menu between "Pin chat" and "Unpin chat". */
  isPinned?: boolean;
  /** The row is being renamed — the label is replaced by the editor. */
  isRenaming?: boolean;
  /** Stored title, passed straight to the editor. Null opens it empty. */
  rawTitle?: string | null;
  /** Omit to render a plain row: the New chat / Search chat actions do. */
  onAction?: (action: ChatRowAction) => void;
  onRename?: (title: string) => void;
  onCancelRename?: () => void;
}

export const ChatSidebarItem = ({
  label,
  isActive = false,
  icon,
  onClick,
  isPinned = false,
  isRenaming = false,
  rawTitle = null,
  onAction,
  onRename,
  onCancelRename,
}: ChatSidebarItemProps) => {
  const theme = useTheme();

  // The editor owns the whole row: there is nothing to pin or delete
  // mid-rename, and the kebab would only be a way to lose the edit.
  if (isRenaming) {
    return (
      <Stack
        direction="row"
        sx={{ width: "100%", alignItems: "center", p: "4px 8px" }}
      >
        <ChatRowTitleEditor
          initialTitle={rawTitle}
          onCommit={(title) => onRename?.(title)}
          onCancel={() => onCancelRename?.()}
        />
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      sx={{
        width: "100%",
        alignItems: "center",
        borderRadius: "4px",
        backgroundColor: isActive
          ? theme.palette.neutral["010"]
          : "transparent",
        ":hover": {
          backgroundColor: theme.palette.neutral["005"],
          // Hover is CSS, not React state — the row must not re-render (or the
          // list re-reconcile) just to reveal a button.
          ".chat-row-menu": { opacity: 1 },
        },
      }}
    >
      {/* The label is the button; the kebab is its sibling. Nesting a button
          inside a button is invalid HTML and swallows the inner click. */}
      <Stack
        component="button"
        type="button"
        direction="row"
        onClick={onClick}
        sx={{
          flex: 1,
          minWidth: 0,
          alignItems: "center",
          gap: "12px",
          p: icon ? "4px 8px" : "8px",
          border: "none",
          borderRadius: "4px",
          textAlign: "left",
          cursor: "pointer",
          backgroundColor: "transparent",
          color: theme.palette.lightBg.high,
        }}
      >
        {icon}
        <Typography variant="body-small" color="inherit" noWrap>
          {label}
        </Typography>
      </Stack>

      {onAction && (
        <DropdownMenu
          options={menuOptions(isPinned)}
          anchorOriginHorizontal="right"
          onOptionClick={({ value }) => {
            if (isChatRowAction(value)) onAction(value);
          }}
        >
          {(isOpen) => (
            <IconButton
              aria-label={`Options for ${label}`}
              aria-expanded={isOpen}
              className="chat-row-menu"
              sx={{
                flexShrink: 0,
                p: "2px",
                mr: "4px",
                color: theme.palette.lightBg.medium,
                // Opacity rather than display, so revealing the kebab never
                // reflows the label. An open menu pins it visible — that is
                // menu state, not hover state, so it is the one JS-driven bit.
                opacity: isOpen ? 1 : 0,
                ":focus-visible": { opacity: 1 },
              }}
            >
              <Horizontal3DotsIcon sx={{ fontSize: "18px" }} />
            </IconButton>
          )}
        </DropdownMenu>
      )}
    </Stack>
  );
};
